import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { api } from "./api"
import type { PaginatedResponse } from "@/types/api"

// Disability Organization Types
export interface DisabilityOrganization {
  id: string
  code: string
  name: string
  short_name: string | null
  organization_type: string
  website: string | null
  email: string | null
  phone: string | null
  address: string | null
  description: string | null
  services: string[]
  target_audience: string[]
  // true when the org belongs to this tenant (created here) vs a read-only
  // global catalog entry — drives whether the delete action is offered.
  is_custom: boolean
}

// Canonical organization-type taxonomy shared with the backend
// (create_disability_organization validation) and the stat cards.
export const DISABILITY_ORG_TYPES = ["agefiph", "fiphfp", "mdph", "cap_emploi", "other"] as const
export type DisabilityOrgType = (typeof DISABILITY_ORG_TYPES)[number]

export interface CreateDisabilityOrganizationRequest {
  name: string
  organization_type: DisabilityOrgType
  short_name?: string
  website?: string
  email?: string
  phone?: string
  address?: string
  description?: string
}

export interface TenantOrganizationContact {
  organization_id: string
  organization_name: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  contact_address: string | null
  local_office_name: string | null
  local_office_code: string | null
  notes: string | null
}

export interface UpdateTenantOrganizationContactRequest {
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  contact_address?: string
  local_office_name?: string
  local_office_code?: string
  notes?: string
}

export interface DisabilityReferral {
  id: string
  contact_id: string
  contact_name: string
  organization_id: string
  organization_name: string
  organization_code: string
  referral_date: string
  referral_reason: string | null
  status: string
  organization_contact_name: string | null
  organization_contact_email: string | null
  organization_contact_phone: string | null
  outcome_date: string | null
  outcome_notes: string | null
  was_successful: boolean | null
  follow_up_date: string | null
  follow_up_notes: string | null
  created_at: string
}

export interface CreateReferralRequest {
  organization_id: string
  referral_date?: string
  referral_reason?: string
  organization_contact_name?: string
  organization_contact_email?: string
  organization_contact_phone?: string
  internal_notes?: string
}

// Disability Organizations API
// The endpoint returns a bare array (like /account-types), not a { data } envelope.
export function listDisabilityOrganizations(): Promise<DisabilityOrganization[]> {
  return api.get<DisabilityOrganization[]>("/disability-organizations")
}

export function getDisabilityOrganization(id: string): Promise<DisabilityOrganization> {
  return api.get<DisabilityOrganization>(`/disability-organizations/${id}`)
}

export function createDisabilityOrganization(
  data: CreateDisabilityOrganizationRequest
): Promise<DisabilityOrganization> {
  return api.post<DisabilityOrganization>("/disability-organizations", data)
}

export function deleteDisabilityOrganization(id: string): Promise<void> {
  return api.delete<void>(`/disability-organizations/${id}`)
}

export function getOrganizationContact(id: string): Promise<TenantOrganizationContact> {
  return api.get<TenantOrganizationContact>(`/disability-organizations/${id}/contact`)
}

export function updateOrganizationContact(
  id: string,
  data: UpdateTenantOrganizationContactRequest
): Promise<TenantOrganizationContact> {
  return api.put<TenantOrganizationContact>(`/disability-organizations/${id}/contact`, data)
}

// Disability Referrals API
export function listReferrals(params?: {
  page?: number
  per_page?: number
  status?: string
  organization_id?: string
  contact_id?: string
}): Promise<PaginatedResponse<DisabilityReferral>> {
  return api.get<PaginatedResponse<DisabilityReferral>>("/disability-referrals", params)
}

export function getReferral(id: string): Promise<DisabilityReferral> {
  return api.get<DisabilityReferral>(`/disability-referrals/${id}`)
}

export function createReferral(contactId: string, data: CreateReferralRequest): Promise<DisabilityReferral> {
  return api.post<DisabilityReferral>(`/contacts/${contactId}/disability-referrals`, data)
}

export function deleteReferral(id: string): Promise<void> {
  return api.delete<void>(`/disability-referrals/${id}`)
}

/** Orientation d'un contact. Textes de santé absents pour un non-lecteur. */
export type ContactReferral = {
  id: string
  organizationName: string
  referralDate: string
  /** Code servi par l'API : pending, contacted, in_progress, completed, cancelled. */
  status: string
  reason: O.Option<string>
  outcomeNotes: O.Option<string>
  followUpNotes: O.Option<string>
}

/** Orientation telle que servie : textes absents à `null`. */
type ContactReferralWire = {
  id: string
  organization_name: string
  referral_date: string
  status: string
  referral_reason: unknown
  outcome_notes: unknown
  follow_up_notes: unknown
}

function decodeContactReferral(wire: ContactReferralWire): ContactReferral {
  return {
    id: wire.id,
    organizationName: wire.organization_name,
    referralDate: wire.referral_date,
    status: wire.status,
    reason: wireText(wire.referral_reason),
    outcomeNotes: wireText(wire.outcome_notes),
    followUpNotes: wireText(wire.follow_up_notes),
  }
}

/**
 * Orientations d'un contact, chacune journalisée si un texte est servi.
 * @example listContactReferrals(contactId).then((referrals) => referrals.length)
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function listContactReferrals(contactId: string): Promise<ContactReferral[]> {
  return api
    .get<ContactReferralWire[]>(`/contacts/${contactId}/disability-referrals`)
    .then((referrals) => referrals.map(decodeContactReferral))
}

/** Statuts d'une orientation, dans l'ordre du suivi. */
export const REFERRAL_STATUSES = ["pending", "contacted", "in_progress", "completed", "cancelled"] as const

export type ReferralStatus = (typeof REFERRAL_STATUSES)[number]

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
  pending: "À contacter",
  contacted: "Contacté",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
}

export function isReferralStatus(code: string): code is ReferralStatus {
  return Object.hasOwn(REFERRAL_STATUS_LABELS, code)
}

/**
 * Libellé du statut ; un code inconnu s'affiche tel quel.
 * @example referralStatusLabel("in_progress") // "En cours"
 */
export function referralStatusLabel(code: string): string {
  return isReferralStatus(code) ? REFERRAL_STATUS_LABELS[code] : code
}

/** Suivi d'une orientation : statut, compte rendu, suivi. */
export type ReferralFollowUp = {
  status: ReferralStatus
  outcomeNotes: string
  followUpNotes: string
}

/**
 * Met à jour le suivi d'une orientation.
 * Textes de santé, écrits sous consentement.
 * @example updateContactReferral(referralId, { status: "contacted", outcomeNotes: "", followUpNotes: "" })
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function updateContactReferral(referralId: string, followUp: ReferralFollowUp): Promise<void> {
  return api
    .put<unknown>(`/disability-referrals/${referralId}`, {
      status: followUp.status,
      outcome_notes: followUp.outcomeNotes,
      follow_up_notes: followUp.followUpNotes,
    })
    .then(() => undefined)
}

/** Nouvelle orientation : organisme, date, motif facultatif. */
export type NewContactReferral = {
  organizationId: string
  referralDate: string
  reason: O.Option<string>
}

/**
 * Oriente un contact vers un organisme d'accompagnement.
 * MDPH, Cap Emploi, AGEFIPH ou autre.
 * @example createContactReferral(contactId, { organizationId, referralDate: "2026-10-11", reason: O.none })
 */
export function createContactReferral(
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  contactId: string,
  referral: NewContactReferral,
): Promise<DisabilityReferral> {
  const reason = pipe(
    referral.reason,
    O.match(
      () => ({}),
      (text) => ({ referral_reason: text }),
    ),
  )
  return createReferral(contactId, {
    organization_id: referral.organizationId,
    referral_date: referral.referralDate,
    ...reason,
  })
}

// Profil d'accessibilité d'un contact (indicateur 26).

/** Besoin d'accessibilité déclaré pour un contact. */
export type AccessibilityNeed = {
  id: string
  contact_id: string
  /** Diagnostic : nul hors administrateurs et référent handicap. */
  disability_type_id: string | null
  disability_type_code: string | null
  disability_type_label: string | null
  description: string | null
  specific_needs: string | null
  rqth_expiry_date: string | null
  referent_notified_at: string | null
  created_at: string
  /** Qui lit ce besoin : public, interne, confidentiel. */
  visibility_level_id: string
  visibility_level_code: string
  visibility_level_label: string
}

/**
 * Titre d'un besoin : son type de handicap.
 * Diagnostic réservé : un titre neutre.
 * @example accessibilityNeedTitle(need) // "Déficience auditive"
 */
export function accessibilityNeedTitle(need: AccessibilityNeed): string {
  return pipe(
    O.fromNullable(need.disability_type_label),
    O.getOrElse(() => "Besoin d'accessibilité (diagnostic réservé au référent handicap)"),
  )
}

/** Adaptation prévue pour un besoin ; `validated_at` posé à la validation. */
export type Accommodation = {
  id: string
  accessibility_need_id: string
  accommodation_type_id: string
  accommodation_type_code: string
  accommodation_type_label: string
  accommodation_category: string | null
  description: string | null
  is_mandatory: boolean
  validated_at: string | null
  created_at: string
}

export type ContactAccessibilityProfile = {
  contact_id: string
  contact_name: string
  has_disability: boolean
  /** Lecteur du diagnostic : il voit aussi son journal. */
  diagnosis_readable: boolean
  accessibility_needs: AccessibilityNeed[]
  accommodations: Accommodation[]
  /** Déclaration du portail, servie aux seuls lecteurs : lire par `portalDeclaration`. */
  portal_declaration: unknown
  /** Preuve du consentement, servie aux seuls lecteurs : lire par `healthConsentProof`. */
  health_consent: unknown
}

/** Canal par lequel le consentement a été recueilli. */
export type HealthConsentChannel = { id: string; code: string; label: string }

/** Texte courant du consentement, durée de conservation de l'organisme comprise. */
export type HealthConsentText = {
  version: string
  /** Version écran, une phrase par ligne. */
  shortLines: string[]
  fullText: string
  channels: HealthConsentChannel[]
}

type HealthConsentTextWire = {
  version: string
  short_text: string
  full_text: string
  channels: HealthConsentChannel[]
}

/**
 * Décode le texte du consentement à la frontière.
 * @example decodeHealthConsentText(wire).shortLines
 */
function decodeHealthConsentText(wire: HealthConsentTextWire): HealthConsentText {
  return {
    version: wire.version,
    shortLines: wire.short_text.split("\n").filter((line) => line.trim() !== ""),
    fullText: wire.full_text,
    channels: wire.channels,
  }
}

/**
 * Texte du consentement, rempli pour l'organisme et ce stagiaire.
 * La base légale suit le financement des inscriptions.
 * @example getHealthConsentText(contactId).then((text) => text.version)
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function getHealthConsentText(contactId: string): Promise<HealthConsentText> {
  return api
    // comply-ignore: api-route-version-prefix — le client `api` préfixe déjà /api/v1.
    .get<HealthConsentTextWire>("/accessibility/health-consent-text", { contact_id: contactId })
    .then(decodeHealthConsentText)
}

/**
 * Texte servi au portail, absent tant qu'il est incomplet.
 * Le portail ignore les canaux : ils ne sont pas lus.
 * @example decodeServedConsentText(response.data.text)
 */
export function decodeServedConsentText(wire: unknown): O.Option<HealthConsentText> {
  return pipe(
    O.fromNullable(wire),
    O.filter(isWireRecord),
    O.chain((text) =>
      pipe(
        O.Do,
        O.apS("version", wireText(text.version)),
        O.apS("short_text", wireText(text.short_text)),
        O.apS("full_text", wireText(text.full_text)),
      ),
    ),
    O.map((text) => decodeHealthConsentText({ ...text, channels: [] })),
  )
}

/** Preuve du consentement en cours. */
export type HealthConsentProof = {
  version: string
  givenAt: string
  channelLabel: string
  recordedBy: O.Option<string>
}

/**
 * Décode une preuve servie, absente sans consentement.
 * @example decodeHealthConsentProof(profile.health_consent)
 */
export function decodeHealthConsentProof(wire: unknown): O.Option<HealthConsentProof> {
  return pipe(
    O.fromNullable(wire),
    O.filter(isWireRecord),
    O.chain((proof) =>
      pipe(
        O.Do,
        O.apS("version", wireText(proof.version)),
        O.apS("givenAt", wireText(proof.given_at)),
        O.apS("channelLabel", wireText(proof.channel_label)),
        O.map((fields) => ({ ...fields, recordedBy: wireText(proof.recorded_by_name) })),
      ),
    ),
  )
}

/**
 * Preuve du consentement d'un profil, absente pour un non-lecteur.
 * @example pipe(healthConsentProof(profile), O.isSome)
 */
export function healthConsentProof(profile: ContactAccessibilityProfile): O.Option<HealthConsentProof> {
  return decodeHealthConsentProof(profile.health_consent)
}

/**
 * Enregistre le consentement donné hors de Qualneo.
 * @example recordHealthConsent(contactId, channelId)
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function recordHealthConsent(contactId: string, channelId: string): Promise<void> {
  return api.post<unknown>(`/contacts/${contactId}/health-consent`, { channel_id: channelId }).then(() => undefined)
}

/**
 * Retire le consentement : la santé du contact est effacée.
 * @example withdrawHealthConsent(contactId)
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function withdrawHealthConsent(contactId: string): Promise<void> {
  return api.post<unknown>(`/contacts/${contactId}/health-consent/withdraw`, {}).then(() => undefined)
}


/** Ce que le stagiaire a déclaré lui-même dans son portail. */
export type PortalDeclaration = {
  disabilityTypes: string[]
  accommodationsNeeded: O.Option<string>
}

function isWireRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

/**
 * Déclaration du portail, absente pour un non-lecteur ou sans déclaration.
 * @example pipe(portalDeclaration(profile), O.isSome)
 */
export function portalDeclaration(profile: ContactAccessibilityProfile): O.Option<PortalDeclaration> {
  return pipe(
    O.fromNullable(profile.portal_declaration),
    O.filter(isWireRecord),
    O.map((wire) => ({
      disabilityTypes: Array.isArray(wire.disability_types)
        ? wire.disability_types.filter((type): type is string => typeof type === "string")
        : [],
      accommodationsNeeded: wireText(wire.accommodations_needed),
    })),
  )
}

// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function getContactAccessibilityProfile(contactId: string): Promise<ContactAccessibilityProfile> {
  return api.get<ContactAccessibilityProfile>(`/contacts/${contactId}/accessibility`)
}

/** Écran ou outil par lequel le diagnostic a été servi. */
export type DiagnosisReadSource =
  | "contact_profile"
  | "contacts_with_needs"
  | "registration"
  | "disability_referral"
  | "accessibility_need"
  | "exam_candidate"
  | "needs_assessment"
  | "enrollment_needs_analysis"

/** Une consultation du diagnostic : qui, quand, quelle fiche. */
export type AccessibilityReadLogEntry = {
  id: string
  readAt: string
  /** Nom du lecteur, sinon son e-mail. Absent si supprimé. */
  reader: O.Option<string>
  readerRole: O.Option<string>
  /** Absent quand le contact a été supprimé. */
  contact: O.Option<{ id: string; name: string }>
  source: DiagnosisReadSource
  /** Inscription ou orientation lue, absente pour le contact. */
  recordId: O.Option<string>
}

/** Ligne telle que servie : champs absents à `null`. */
type AccessibilityReadLogEntryWire = {
  id: string
  read_at: string
  reader_name: unknown
  reader_email: unknown
  reader_role: unknown
  contact_id: unknown
  contact_name: unknown
  source: DiagnosisReadSource
  record_id: unknown
}

/** Texte servi, `O.none` pour `null` ou une chaîne vide. */
function wireText(value: unknown): O.Option<string> {
  return pipe(
    O.fromNullable(value),
    O.filter((found): found is string => typeof found === "string"),
    O.filter((text) => text.trim() !== ""),
  )
}

/** Décode une ligne du journal à la frontière. */
function decodeReadLogEntry(wire: AccessibilityReadLogEntryWire): AccessibilityReadLogEntry {
  return {
    id: wire.id,
    readAt: wire.read_at,
    reader: pipe(
      wireText(wire.reader_name),
      O.alt(() => wireText(wire.reader_email)),
    ),
    readerRole: wireText(wire.reader_role),
    contact: pipe(
      wireText(wire.contact_id),
      O.chain((id) =>
        pipe(
          wireText(wire.contact_name),
          O.map((name) => ({ id, name })),
        ),
      ),
    ),
    source: wire.source,
    recordId: wireText(wire.record_id),
  }
}

/** Filtres du journal ; un filtre absent n'est pas envoyé. */
export type AccessibilityReadLogParams = {
  contactId: O.Option<string>
  fromDate: O.Option<string>
  toDate: O.Option<string>
  page: number
  perPage: number
}

/**
 * Journal des consultations, réservé aux lecteurs du diagnostic.
 * @example listAccessibilityReadLog({ contactId: O.some(id), fromDate: O.none, toDate: O.none, page: 1, perPage: 20 })
 */
export function listAccessibilityReadLog(
  params: AccessibilityReadLogParams,
): Promise<PaginatedResponse<AccessibilityReadLogEntry>> {
  const filters: [string, O.Option<string>][] = [
    ["contact_id", params.contactId],
    ["from_date", params.fromDate],
    ["to_date", params.toDate],
  ]
  const query = Object.fromEntries([
    ["page", String(params.page)],
    ["per_page", String(params.perPage)],
    ...filters.flatMap(([key, value]) =>
      pipe(
        value,
        O.match(
          (): [string, string][] => [],
          (filterValue): [string, string][] => [[key, filterValue]],
        ),
      ),
    ),
  ])
  return api
    // comply-ignore: api-route-version-prefix — le client `api` préfixe déjà /api/v1.
    .get<PaginatedResponse<AccessibilityReadLogEntryWire>>("/accessibility/read-log", query)
    .then((page) => ({ ...page, data: page.data.map(decodeReadLogEntry) }))
}

/** Stagiaire à besoins, servi aux seuls lecteurs du diagnostic. */
export type ContactWithNeeds = {
  id: string
  name: string
  disabilityTypes: string[]
  accommodationCount: number
}

type ContactWithNeedsWire = {
  contact_id: string
  contact_name: string
  disability_types: string[]
  accommodation_count: number
}

/** Filtres de la liste ; une recherche vide n'est pas envoyée. */
export type ContactsWithNeedsParams = {
  search: O.Option<string>
  page: number
  perPage: number
}

/**
 * Stagiaires à besoins d'accessibilité, chaque stagiaire servi journalisé ; 403 hors lecteurs.
 * @example listContactsWithNeeds({ search: O.none, page: 1, perPage: 20 })
 */
export function listContactsWithNeeds(
  params: ContactsWithNeedsParams,
): Promise<PaginatedResponse<ContactWithNeeds>> {
  const search = pipe(
    params.search,
    O.match(
      () => ({}),
      (text) => ({ q: text }),
    ),
  )
  return api
    // comply-ignore: api-route-version-prefix — le client `api` préfixe déjà /api/v1.
    .get<PaginatedResponse<ContactWithNeedsWire>>("/accessibility/contacts", {
      page: String(params.page),
      per_page: String(params.perPage),
      ...search,
    })
    .then((page) => ({
      ...page,
      data: page.data.map((wire) => ({
        id: wire.contact_id,
        name: wire.contact_name,
        disabilityTypes: wire.disability_types,
        accommodationCount: wire.accommodation_count,
      })),
    }))
}

// Valide une adaptation : `validated_at` posé par le serveur.
export function validateAccommodation(
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  contactId: string,
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  accommodationId: string,
): Promise<Accommodation> {
  return api.post<Accommodation>(`/contacts/${contactId}/accessibility/accommodations/${accommodationId}/validate`, { validated: true })
}

/** Entrée d'un référentiel d'accessibilité. Type de handicap, d'adaptation, ou visibilité. */
export type AccessibilityReference = {
  id: string
  code: string
  label: string
  description: string | null
}

/** Type d'adaptation, rangé par catégorie. */
export type AccommodationType = AccessibilityReference & { category: string | null }

/** Niveau de visibilité : 0 public, plus haut plus restreint. */
export type VisibilityLevel = AccessibilityReference & { access_level: number }

export function listDisabilityTypes(): Promise<AccessibilityReference[]> {
  return api.get<AccessibilityReference[]>("/accessibility/disability-types")
}

export function listAccommodationTypes(): Promise<AccommodationType[]> {
  return api.get<AccommodationType[]>("/accessibility/accommodation-types")
}

export function listVisibilityLevels(): Promise<VisibilityLevel[]> {
  return api.get<VisibilityLevel[]>("/accessibility/visibility-levels")
}

/** Corps de la déclaration d'un besoin. Textes vides omis. */
export type RecordAccessibilityNeedRequest = {
  disability_type_id: string
  visibility_level_id: string
  description?: string
  specific_needs?: string
  rqth_document_url?: string
  rqth_expiry_date?: string
}

// Déclare un besoin. Le serveur prévient le référent handicap.
export function recordAccessibilityNeed(
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  contactId: string,
  request: RecordAccessibilityNeedRequest,
  /** Canal du consentement recueilli ici, faute de consentement actif. */
  consentChannelId: O.Option<string>,
): Promise<AccessibilityNeed> {
  const consent = pipe(
    consentChannelId,
    O.match(
      () => ({}),
      (channelId) => ({ consent_channel_id: channelId }),
    ),
  )
  return api.post<AccessibilityNeed>(`/contacts/${contactId}/accessibility/needs`, { ...request, ...consent })
}

export type AddAccommodationRequest = {
  accessibility_need_id: string
  accommodation_type_id: string
  description?: string
  is_mandatory: boolean
}

export function addAccommodation(
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  contactId: string,
  request: AddAccommodationRequest,
): Promise<Accommodation> {
  return api.post<Accommodation>(`/contacts/${contactId}/accessibility/accommodations`, request)
}

// Retire un besoin ; ses adaptations partent avec lui.
export function removeAccessibilityNeed(
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  contactId: string,
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  needId: string,
): Promise<void> {
  return api.delete(`/contacts/${contactId}/accessibility/needs/${needId}`)
}

export function removeAccommodation(
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  contactId: string,
  // comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
  accommodationId: string,
): Promise<void> {
  return api.delete(`/contacts/${contactId}/accessibility/accommodations/${accommodationId}`)
}
